import { AppLayout } from '@/components/layouts/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { fetchCarById } from '@/services/cars'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { formatDate } from '@/lib/utils'
import { ArrowLeft, Building2, Calendar, FileText, FolderKanban } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

const CarDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: car, isLoading } = useQuery({
    queryKey: ['car', id],
    queryFn: () => fetchCarById(id!),
    enabled: !!id,
  })

  useDocumentTitle(car?.registrationNumber)

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64" />
        </div>
      </AppLayout>
    )
  }

  if (!car) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Bil hittades inte</p>
        </div>
      </AppLayout>
    )
  }

  // const totalUnitPrice = car.invoices.reduce((sum, invoice) => {
  //   return sum + (invoice.lineItems.reduce((itemSum, item) => itemSum + (item.unitPrice || 0), 0) || 0)
  // }, 0)

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold">{car.registrationNumber}</h1>
          <p className="text-muted-foreground mt-1">Bildetaljer</p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Verkstad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold capitalize">{car.workshop || 'Ingen verkstad'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Senast uppdaterad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">
                {car.updatedAt ? format(new Date(car.updatedAt), 'd MMMM yyyy', { locale: sv }) : '-'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Antal fakturor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">{car.invoices.length}</p>
            </CardContent>
          </Card>

          {car.cases.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <FolderKanban className="h-4 w-4" />
                  Delcheck
                </CardTitle>
              </CardHeader>
              <CardContent>
                {car.cases.sort().map(c => (
                  <Link key={c} to={`/cases/${c}`}>
                    <Badge variant="secondary">{c}</Badge>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Fakturor</CardTitle>
          </CardHeader>
          <CardContent>
            {car.invoices.map(invoice => {
              const lineArray = Array.isArray(invoice.lineItems) ? invoice.lineItems : []
              return (
                <div key={invoice.id} className="mb-8 last:mb-0">
                  <div className="mb-4 pb-2 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">Faktura {invoice?.invoiceNumber}</h3>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(invoice?.invoiceDate, 'd MMM yyyy', '')}{' '}
                          - {invoice.vendor} ({invoice.vendorOrganizationNumber || 'Org.nr saknas'})
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/invoices/${invoice.id}?carId=${car.id}`)}
                      >
                        Visa faktura
                      </Button>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Beskrivning</TableHead>
                        <TableHead className="w-[80px] text-right">Antal</TableHead>
                        <TableHead className="w-[120px] text-right">Enhetspris</TableHead>
                        <TableHead className="w-[120px] text-right">Totalpris</TableHead>
                        <TableHead className="w-[60px] text-right"></TableHead>
                        <TableHead className="w-[120px] text-right opacity-60">Rabattpris</TableHead>
                        <TableHead className="w-[120px] text-right opacity-60">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineArray.map((line, index) => (
                        <TableRow key={`invoice-${invoice.id}-line-${index}`}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell className="text-right">{Number(line.quantity)}</TableCell>
                          <TableCell className="text-right">
                            {Number(line.unitPrice).toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                            })}
                          </TableCell>
                          <TableCell className="text-right">
                            {Number(line.unitPrice * line.quantity).toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                            })}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right opacity-60">
                            {line.amountExclVat
                              ? Number(line.amountExclVat).toLocaleString('sv-SE', {
                                  style: 'currency',
                                  currency: 'SEK',
                                })
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right opacity-60">
                            {line.amountExclVat
                              ? Number(line.amountExclVat * line.quantity).toLocaleString('sv-SE', {
                                  style: 'currency',
                                  currency: 'SEK',
                                })
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            })}

            {car.invoices.length === 0 && (
              <p className="text-center text-muted-foreground py-8">Inga fakturarader hittades</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}

export default CarDetail
